import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useRecipe } from '../hooks/useRecipes';
import { useWakeLock } from '../hooks/useWakeLock';
import { useFullscreen } from '../hooks/useFullscreen';
import { scaleQuantity, formatQuantity } from '../lib/scale';

type LocationState = { servings?: number } | null;

export default function CookModePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialServings = (location.state as LocationState)?.servings;
  const { data: recipe, loading, error } = useRecipe(id);
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { enter, exit } = useFullscreen();
  const { active: wakeLockActive } = useWakeLock(true);

  useEffect(() => {
    if (containerRef.current) enter(containerRef.current);
    return () => {
      exit();
    };
  }, [enter, exit]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const total = recipe?.steps.length ?? 1;
      if (e.key === 'ArrowRight' || e.key === ' ')
        setIndex((i) => Math.min(i + 1, total - 1));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      else if (e.key === 'Escape') navigate(`/recipe/${id}`);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id, navigate, recipe?.steps.length]);

  const touchStartX = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    const total = recipe?.steps.length ?? 1;
    if (Math.abs(dx) > 60) {
      if (dx < 0) setIndex((i) => Math.min(i + 1, total - 1));
      else setIndex((i) => Math.max(0, i - 1));
    }
    touchStartX.current = null;
  }

  if (loading) {
    return (
      <main className="flex min-h-[100svh] items-center justify-center">
        <p className="text-muted">preparazione…</p>
      </main>
    );
  }
  if (error || !recipe || recipe.steps.length === 0) {
    return (
      <main className="flex min-h-[100svh] items-center justify-center flex-col gap-6 px-6 text-center">
        <p className="text-lg font-bold text-brand text-balance">
          {error ?? 'Questa ricetta non ha passaggi da seguire.'}
        </p>
        <Link to={`/recipe/${id}`} className="btn-soft">← torna</Link>
      </main>
    );
  }

  const currentServings = initialServings ?? recipe.baseServings;
  const step = recipe.steps[index];
  const total = recipe.steps.length;

  return (
    <div
      ref={containerRef}
      className="min-h-[100svh] flex flex-col relative bg-bg"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <header className="px-5 pt-5 flex items-start justify-between shrink-0">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted">
            {recipe.name}
          </div>
          <div className="font-mono text-[11px] text-soft tabular-nums mt-0.5">
            passaggio {index + 1} di {total}
            {wakeLockActive && <span className="ml-2 text-sage">• display acceso</span>}
          </div>
        </div>
        <button
          onClick={() => navigate(`/recipe/${id}`)}
          className="h-9 w-9 rounded-full bg-surface flex items-center justify-center"
          aria-label="chiudi"
        >
          ✕
        </button>
      </header>

      <div className="mt-4 mx-5 h-1.5 rounded-full bg-surface overflow-hidden shrink-0">
        <div
          className="h-full bg-brand transition-all duration-500 rounded-full"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      {/* Ingredienti sempre visibili — compatti, griglia 2 colonne */}
      <aside className="px-5 mt-4 shrink-0">
        <div className="card p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-xs font-bold uppercase tracking-wider text-ink">
              Ingredienti
            </h2>
            <span className="text-[11px] text-muted font-medium">per {currentServings} {currentServings === 1 ? 'persona' : 'persone'}</span>
          </div>
          <ul className="grid grid-cols-2 gap-x-5 gap-y-1 max-h-[24vh] overflow-auto">
            {recipe.ingredients.map((ing) => {
              const q = scaleQuantity(ing.quantity, currentServings, recipe.baseServings);
              return (
                <li
                  key={ing.id}
                  className="flex items-baseline justify-between gap-2 py-1 text-[14px]"
                >
                  <span className="font-medium truncate">{ing.name}</span>
                  <span className="text-muted font-mono tabular-nums text-[13px] shrink-0">
                    {formatQuantity(q)}
                    {ing.unit ? ` ${ing.unit}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      <main className="flex-1 flex items-center justify-center px-6 py-6 min-h-0">
        <div key={index} className="max-w-2xl w-full space-y-6 animate-riseIn text-center">
          <div className="mx-auto h-16 w-16 md:h-20 md:w-20 rounded-3xl bg-brand text-white font-extrabold flex items-center justify-center text-3xl md:text-4xl tabular-nums shadow-brand">
            {index + 1}
          </div>
          <p className="text-[clamp(1.35rem,4.2vw,2.1rem)] font-extrabold tracking-tightest leading-[1.25] text-ink text-pretty">
            {step.content}
          </p>
        </div>
      </main>

      <nav className="px-5 pb-[max(env(safe-area-inset-bottom),1rem)] pt-2 grid grid-cols-[64px_1fr] gap-3 shrink-0">
        <button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className="h-14 rounded-full bg-surface hover:bg-line disabled:opacity-30 flex items-center justify-center text-2xl font-bold"
          aria-label="precedente"
        >
          ←
        </button>
        <button
          onClick={() =>
            index === total - 1
              ? navigate(`/recipe/${id}`)
              : setIndex((i) => Math.min(total - 1, i + 1))
          }
          className="h-14 btn-brand text-base flex items-center justify-center gap-2"
          aria-label={index === total - 1 ? 'fine' : 'successivo'}
        >
          {index === total - 1 ? 'È pronto ✓' : 'Avanti'}
          {index < total - 1 && <span className="text-xl">→</span>}
        </button>
      </nav>
    </div>
  );
}
